/**
 * Generative animated mesh gradient for marketing/public surfaces.
 * Dispatches between CSS blob renderer and WebGL mesh renderer based on config.
 * Honors prefers-reduced-motion via CSS in marketing.css.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useMeshGradientConfig } from "@/hooks/useMeshGradientConfig";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  speedToDurationSec,
  type MeshConfig,
  type MeshGradientType,
  type MeshSlot,
} from "@/lib/meshGradient";
import {
  buildShapeSvg,
  svgToDataUri,
  type ShapeMaskConfig,
} from "@/lib/meshShapeMask";
import { WebGLMeshBackground } from "./WebGLMeshBackground";


const MOTION_KEYFRAMES: Record<NonNullable<MeshConfig["motion"]>, string[]> = {
  drift: ["mkt-float-1", "mkt-float-2", "mkt-float-3"],
  swirl: ["mkt-swirl-1", "mkt-swirl-2", "mkt-swirl-3"],
  pulse: ["mkt-pulse-1", "mkt-pulse-2", "mkt-pulse-3"],
  still: [],
};

const slotBackground = (slot: MeshSlot, fallbackType: MeshGradientType): string => {
  const shape = slot.shape ?? fallbackType;
  const rot = slot.rotation ?? 0;
  switch (shape) {
    case "linear":
      return `linear-gradient(${135 + rot}deg, ${slot.hex} 0%, transparent 70%)`;
    case "conic":
      return `conic-gradient(from ${rot}deg, ${slot.hex} 0%, transparent 60%)`;
    case "radial":
    default:
      return `radial-gradient(circle at center, ${slot.hex} 0%, transparent 70%)`;
  }
};

// Inline SVG noise overlay (no external file required).
const GrainOverlay = ({ intensity }: { intensity: number }) => {
  if (intensity <= 0) return null;
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>`;
  const url = `data:image/svg+xml;utf8,${svg.replace(/#/g, "%23")}`;
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        backgroundImage: `url("${url}")`,
        backgroundRepeat: "repeat",
        opacity: intensity,
        mixBlendMode: "overlay",
        pointerEvents: "none",
      }}
    />
  );
};

type Props = { config?: MeshConfig; contained?: boolean; forceMobile?: boolean };

export const MeshBackground = ({ config: override, contained = false, forceMobile = false }: Props) => {
  const live = useMeshGradientConfig();
  const baseConfig = override ?? live;
  const detectedMobile = useIsMobile();
  const isMobile = forceMobile || detectedMobile;
  // Phones get the CSS blob renderer regardless of admin config — Three.js never
  // initializes a WebGL context on mobile (no GPU compositing / rAF / shader compile).
  // Heart mask is unchanged because shape rendering happens below this branch.
  const useWebGL = baseConfig.renderer === "webgl-mesh" && !isMobile;

  // Apply mobile-only overrides (admin → Mesh Gradient → Mobile overrides)
  // when on phones AND not rendering WebGL.
  const config: MeshConfig = useMemo(() => {
    const mob = baseConfig.mobile;
    if (!isMobile || useWebGL || !mob?.enabled) return baseConfig;
    const mult = typeof mob.opacityMultiplier === "number" ? mob.opacityMultiplier : 1;
    const blurDelta = typeof mob.blurDelta === "number" ? mob.blurDelta : 0;
    const hexes = mob.slotHexes ?? [];
    return {
      ...baseConfig,
      baseColor: mob.baseColor ?? baseConfig.baseColor,
      blend: mob.blend ?? baseConfig.blend,
      blur: Math.max(0, Math.min(200, baseConfig.blur + blurDelta)),
      slots: baseConfig.slots.map((s, i) => {
        const override = hexes[i];
        const hex = typeof override === "string" && override.trim() ? override : s.hex;
        return {
          ...s,
          hex,
          opacity: Math.max(0, Math.min(1, s.opacity * mult)),
        };
      }),
    };
  }, [baseConfig, isMobile, useWebGL]);




  const rootRef = useRef<HTMLDivElement | null>(null);
  const [dims, setDims] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setDims({ w: Math.max(1, Math.round(r.width)), h: Math.max(1, Math.round(r.height)) });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const rootStyle: React.CSSProperties = contained
    ? { position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }
    : {};

  const shape = config.shape;
  const shapeOn = !!shape?.enabled && dims.w > 0 && dims.h > 0;
  const maskMode = shape?.mode ?? "mask";

  // Pre-build SVG strings keyed off measured dimensions + shape config.
  const maskSvg = useMemo(
    () => (shapeOn && shape ? buildShapeSvg(shape, true, dims.w, dims.h) : ""),
    [shapeOn, shape, dims.w, dims.h],
  );
  const silhouetteSvg = useMemo(
    () => (shapeOn && shape ? buildShapeSvg(shape, false, dims.w, dims.h) : ""),
    [shapeOn, shape, dims.w, dims.h],
  );

  const meshLayer = (
    <>
      {useWebGL ? (
        <WebGLMeshBackground webgl={config.webgl} motion={config.motion} />
      ) : (
        <CssBlobLayer config={config} contained={contained} />
      )}
    </>
  );


  return (
    <div
      ref={rootRef}
      className={contained ? undefined : "mkt-mesh-root"}
      style={rootStyle}
      aria-hidden="true"
    >
      {/* Base color */}
      <div style={{ position: "absolute", inset: 0, background: config.baseColor || "transparent" }} />

      {/* Extend mesh outside shape — unmasked copy of the mesh behind the shape (mask mode only) */}
      {shapeOn && maskMode === "mask" && shape?.extendOutside?.enabled && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            opacity: Math.max(0, Math.min(1, shape.extendOutside.intensity)),
            pointerEvents: "none",
          }}
        >
          {meshLayer}
        </div>
      )}

      {/* Halo glow behind the mesh, separate full-bleed layer (only in "mask" mode) */}
      {shapeOn && maskMode === "mask" && shape?.halo.enabled && (
        <HaloLayer shape={shape} w={dims.w} h={dims.h} />
      )}


      {/* Renderer (optionally masked) */}
      {shapeOn && maskMode === "mask" && shape ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            WebkitMaskImage: svgToDataUri(maskSvg),
            maskImage: svgToDataUri(maskSvg),
            WebkitMaskRepeat: "no-repeat",
            maskRepeat: "no-repeat",
            WebkitMaskPosition: "0 0",
            maskPosition: "0 0",
            WebkitMaskSize: "100% 100%",
            maskSize: "100% 100%",
          }}
        >
          {meshLayer}
        </div>
      ) : (
        meshLayer
      )}

      {/* Grain */}
      {config.grain.enabled && <GrainOverlay intensity={config.grain.intensity} />}

      {/* Overlay tint */}
      {config.overlay.opacity > 0 && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: config.overlay.hex,
            opacity: config.overlay.opacity,
            pointerEvents: "none",
          }}
        />
      )}

      {/* Silhouette on top — flat color cutout, full-bleed SVG so geometry can't clip. */}
      {shapeOn && maskMode === "silhouette" && shape && (
        <img
          src={`data:image/svg+xml;utf8,${encodeURIComponent(silhouetteSvg)}`}
          alt=""
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            opacity: shape.intensity,
            pointerEvents: "none",
          }}
        />
      )}

      {/* Vignette — darken everything outside the shape */}
      {shapeOn && maskMode === "vignette" && shape && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: config.baseColor || "#000",
            opacity: shape.intensity,
            pointerEvents: "none",
            WebkitMaskImage: `${svgToDataUri(maskSvg)}, linear-gradient(#000,#000)`,
            maskImage: `${svgToDataUri(maskSvg)}, linear-gradient(#000,#000)`,
            WebkitMaskRepeat: "no-repeat, no-repeat",
            maskRepeat: "no-repeat, no-repeat",
            WebkitMaskPosition: "0 0, 0 0",
            maskPosition: "0 0, 0 0",
            WebkitMaskSize: "100% 100%, 100% 100%",
            maskSize: "100% 100%, 100% 100%",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
          }}
        />
      )}
    </div>
  );
};

/**
 * Halo: a separate, full-bleed layer behind the mesh. Renders a scaled-up,
 * extra-feathered copy of the shape into an SVG sized to the container — never
 * a percent-square box, so it can't clip top/bottom in non-square containers.
 */
const HaloLayer = ({
  shape,
  w,
  h,
}: {
  shape: ShapeMaskConfig;
  w: number;
  h: number;
}) => {
  const haloShape: ShapeMaskConfig = useMemo(
    () => ({
      ...shape,
      feather: Math.max(shape.feather, 40),
    }),
    [shape],
  );
  const haloSize = shape.size * shape.halo.size;
  const haloColor = shape.halo.color;
  const svg = useMemo(
    () => buildShapeSvg(haloShape, false, w, h, haloSize, haloColor),
    [haloShape, w, h, haloSize, haloColor],
  );
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        opacity: shape.halo.intensity,
        pointerEvents: "none",
        filter: "blur(20px)",
      }}
    >
      <img
        src={`data:image/svg+xml;utf8,${encodeURIComponent(svg)}`}
        alt=""
        style={{ width: "100%", height: "100%", display: "block" }}
      />
    </div>
  );
};

const CssBlobLayer = ({ config, contained }: { config: MeshConfig; contained: boolean }) => {
  const duration = speedToDurationSec(config.speed);
  const kfs = MOTION_KEYFRAMES[config.motion ?? "drift"];

  return (
    <>
      {config.slots.map((slot, i) => {
        if (!slot.enabled) return null;
        const kf = kfs.length > 0 ? kfs[i % kfs.length] : null;
        const reverse = i % 2 === 1 ? " reverse" : "";
        const dur = duration + (i * 2);
        const aspect = slot.aspect ?? 1;
        const width = slot.size * (aspect >= 1 ? 1 : aspect);
        const height = slot.size * (aspect >= 1 ? 1 / aspect : 1);

        return (
          <div
            key={i}
            className={contained ? undefined : "mkt-mesh-layer"}
            style={{
              position: "absolute",
              borderRadius: 9999,
              willChange: "transform",
              top: `${slot.y}%`,
              left: `${slot.x}%`,
              width: `${width}%`,
              height: `${height}%`,
              background: slotBackground(slot, config.type),
              backgroundColor: (slot.shape ?? config.type) === "radial" ? slot.hex : undefined,
              opacity: slot.opacity,
              filter: `blur(${config.blur}px)`,
              mixBlendMode: slot.blend ?? config.blend,
              animation: kf ? `${kf} ${dur}s ease-in-out infinite${reverse}` : undefined,
            }}
          />
        );
      })}
    </>
  );
};
